import { useMemo, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, Search } from "lucide-react";
import styles from "./admin-dashboard-page.module.css";

const PAGE_SIZE = 10;

const categories = [
  { key: "all", label: "All issues" },
  { key: "designation", label: "Designations" },
  { key: "projectRole", label: "Project roles" },
  { key: "employeeRole", label: "Employee roles" },
  { key: "allocation", label: "Allocations" },
  { key: "other", label: "Other" },
] as const;

type Category = (typeof categories)[number]["key"];

function categoryFor(issue: string): Exclude<Category, "all"> {
  if (issue.includes("unmapped project-member designation")) return "designation";
  if (issue.includes("configured PM") || issue.includes("Delivery Lead") || issue.includes("Head of Delivery")
    || issue.includes("Project Manager") || issue.includes("Duplicate proposed obligation")) return "projectRole";
  if (issue.includes("different roles across projects") || issue.includes("conflicting designations")) return "employeeRole";
  if (issue.includes("allocation") || issue.includes("joining date") || issue.includes("end date")) return "allocation";
  return "other";
}

export default function AssignmentPreviewIssues({ issues }: { issues: string[] }) {
  const [category, setCategory] = useState<Category>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rows = useMemo(() => issues.map((message, index) => ({ message, index, category: categoryFor(message) })), [issues]);
  const counts = useMemo(() => rows.reduce((result, row) => {
    result[row.category] += 1;
    return result;
  }, { designation: 0, projectRole: 0, employeeRole: 0, allocation: 0, other: 0 }), [rows]);
  const filtered = useMemo(() => rows.filter(row =>
    (category === "all" || row.category === category)
    && row.message.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())), [rows, category, search]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const first = (currentPage - 1) * PAGE_SIZE;
  const visible = filtered.slice(first, first + PAGE_SIZE);

  return <section className={styles.issuePanel} aria-labelledby="lens-preview-issues-title">
    <div className={styles.issueHeading}>
      <span className={styles.issueIcon}><AlertTriangle size={20} aria-hidden="true" /></span>
      <div>
        <h3 id="lens-preview-issues-title">{issues.length} issue{issues.length === 1 ? "" : "s"} to resolve</h3>
        <p>Review the assignments below. The queue cannot be built until every issue is resolved.</p>
      </div>
    </div>
    <div className={styles.issueTools}>
      <div className={styles.issueCategories} role="group" aria-label="Filter validation issues by type">
        {categories.map(item => {
          const count = item.key === "all" ? issues.length : counts[item.key];
          return <button key={item.key} type="button" className={category === item.key ? styles.issueCategoryActive : styles.issueCategory}
            aria-pressed={category === item.key} onClick={() => { setCategory(item.key); setPage(1); }}>
            {item.label} <span>{count}</span>
          </button>;
        })}
      </div>
      <label className={styles.issueSearch}>
        <Search size={16} aria-hidden="true" />
        <span className={styles.srOnly}>Search validation issues</span>
        <input type="search" value={search} placeholder="Search by project, employee, or issue" onChange={event => { setSearch(event.target.value); setPage(1); }} />
      </label>
    </div>
    {visible.length ? <ol className={styles.issueList} start={first + 1}>
      {visible.map(row => <li key={row.index} value={row.index + 1}><span className={styles.issueNumber}>{row.index + 1}</span><span>{row.message}</span></li>)}
    </ol> : <p className={styles.issueEmpty}>No issues match this filter. Try another category or search term.</p>}
    <div className={styles.issueFooter}>
      <span aria-live="polite">{filtered.length ? `Showing ${first + 1}–${first + visible.length} of ${filtered.length}` : "Showing 0 of 0"}</span>
      {pageCount > 1 && <div className={styles.issuePagination} role="navigation" aria-label="Validation issue pages">
        <button type="button" disabled={currentPage === 1} aria-label="Previous issue page" onClick={() => setPage(currentPage - 1)}><ChevronLeft size={16} /> Previous</button>
        <span>Page {currentPage} of {pageCount}</span>
        <button type="button" disabled={currentPage === pageCount} aria-label="Next issue page" onClick={() => setPage(currentPage + 1)}>Next <ChevronRight size={16} /></button>
      </div>}
    </div>
  </section>;
}
